/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import cn from 'classnames';
import {rmAllFalsy, removeWhitespace} from 'mad-utils/lib/shared';

/**************************************** TYPE DEFINITIONS ****************************************/
interface ErrorBoundaryProps {
    miscInfo?: string;
    children?: any;
    filename?: string;
    parentName: string;
    childTreeInfo?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: false | Error;
    info: false | {componentStack?: string};
}

/********************************************* EXPORT *********************************************/
/**
 * Display the child property under normal circumstances (acts as a passthrough)
 *
 * If an error throws in one of this component's children, it replaces its child
 * views with a fallback view containing e.g. stacktrace, error location, etc
 * Without this, such errors simply unmount the entire UI tree & display an empty screen
 */
export class RootErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state = {
        hasError: false,
        error: false as false | (Error & {stack?: string}),
        info: false as false | React.ErrorInfo,
    };

    /**
     * Triggers display of fallback UI if error thrown, by changing state.hasError to true
     */
    componentDidCatch(error: Error, info: React.ErrorInfo) {
        this.setState({hasError: true, error, info});
    }

    /**
     * Build the stacktrace component list - both the text and the component list
     */
    buildStack = (stackText: string, isComponentStack: boolean = true) => {
        const stackArray = rmAllFalsy(stackText.split(/[\s↵\n]+(?:in|at)[\s↵\n]+/));

        const splitWord = isComponentStack ? `in` : `at`;

        const stackComponents = stackArray.map((item, idx) => {
            if (!removeWhitespace(item)) return ``;
            return idx === stackArray.length - 1 ? (
                <div key={`${item}_${idx}`}>
                    {` `}
                    \--> {splitWord} {item}
                </div>
            ) : (
                <div key={`${item}_${idx}`}>
                    {` `}
                    |--> {splitWord} {item}
                </div>
            );
        });

        return {stackText, stackComponents};
    };

    /**
     * Remove (most) library references from the error stack (including all
     * CDN-loaded scripts) for display in "clean error stack" section of the UI
     * @param {JSX.Element|""} errStackComponents Stack items to filter
     * @return {JSX.Element|""} Stack items containing no library references
     */
    buildCleanErrorStack = (errStackComponents: ('' | JSX.Element)[]): ('' | JSX.Element)[] =>
        errStackComponents.filter(cmp => {
            const str = (cmp as any).key as string;

            // Exclude react, react-dom, polyglot, moment, mobx, mobx-react, recompose, lodash,
            // es*5+6+7 shims, axios, mad-utils, circular-json, fetch, common-tags,
            // bluebird, reflect-metadata, the vendor bundle & anything loaded from cloudflare
            if (str.includes(/https?:\/\/cdnjs\.cloudflare\.com\//g)) return false;
            if (str.includes(/dll\.vendor\.js/g)) return false;

            if (str.includes(/\/react(-dom)?\.\w+\.?js:\d+:\d+/g)) return false;
            if (str.includes(/\/polyglot\.\w+\.?js:\d+:\d+/g)) return false;
            if (str.includes(/\/moment\.\w+\.?js:\d+:\d+/g)) return false;
            if (str.includes(/\/mobx(\-react)?\.\w+\.?js:\d+:\d+/g)) return false;
            if (str.includes(/\/recompose\.\w+\.?js:\d+:\d+/g)) return false;
            if (str.includes(/\/lodash\.\w+\.?js:\d+:\d+/g)) return false;
            if (str.includes(/\/es[567]\-shim\.\w+\.?js:\d+:\d+/g)) return false;
            if (str.includes(/\/axios\.\w+\.?js:\d+:\d+/g)) return false;
            if (str.includes(/\/mad\-utils\.\w+\.?js:\d+:\d+/g)) return false;
            if (str.includes(/\/circular\-json\.\w+\.?js:\d+:\d+/g)) return false;
            if (str.includes(/\/[a-z\-]+?fetch\.\w+\.?js:\d+:\d+/g)) return false;
            if (str.includes(/\/common\-tags\.\w+\.?js:\d+:\d+/g)) return false;
            if (str.includes(/\/bluebird\.\w+\.?js:\d+:\d+/g)) return false;
            if (str.includes(/\/reflect\-metadata\.\w+\.?js:\d+:\d+/g)) return false;
            return true;
        });

    /**
     * Render the error boundary
     */
    render() {
        const {miscInfo, parentName, filename, children, childTreeInfo} = this.props;
        const {error, info, hasError} = this.state;

        // Render custom fallback UI if error present
        if (hasError) {
            const errMsgText = (error && error.message) || error.toString();

            const {stackText, stackComponents} = this.buildStack(
                (info && info.componentStack) || info.toString()
            );

            const {stackComponents: errStackComponents} = this.buildStack(
                (error && error.stack) || ``,
                false
            );

            return (
                <div className={cn(`m-20`)}>
                    <h1>
                        {filename ? `(${filename}) ` : ``}
                        Rendering error occurred {parentName ? ` ::: In ${parentName} ` : ``}
                        {parentName && childTreeInfo ? ` --[WRAPS]--> ${childTreeInfo} ` : ``}
                        {name ? ` |||| ${miscInfo}` : ``}
                    </h1>
                    <div>
                        <h2>Error:</h2>
                        <p>{errMsgText}</p>
                    </div>
                    <div>
                        <h2 className={cn(`mb-5`)}>Clean error stack:</h2>
                        <div className={cn(`ml-7`)}>
                            {this.buildCleanErrorStack(errStackComponents)}
                        </div>
                    </div>
                    <div>
                        <h2 className={cn(`mb-5`)}>Error stack:</h2>
                        <div className={cn(`ml-7`)}>{errStackComponents}</div>
                    </div>
                    <div>
                        <h2 className={cn(`mb-5`)}>Component stack:</h2>
                        <div className={cn(`ml-7`)}>{stackComponents}</div>
                        <div>
                            <h4 className={cn(`mb-1`)}>Raw component stack:</h4>
                            <div className={cn(`ml-2`)}>{stackText}</div>
                        </div>
                    </div>
                    <div className={cn(`h-50`)} />
                </div>
            );
        }
        // Render standard child UI if no errors present
        return this.props.children;
    }
}
